import { useRef, useState } from 'react';
import { Badge } from '@understory-io/leaf/badge';
import { Checkbox, CheckboxGroup } from '@understory-io/leaf/checkbox';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { FormattedMessage } from 'react-intl';
import { useSearchParams, useSubmit } from 'react-router';
import { css } from '@understory-io/leaf/css';
import { Dialog } from '@understory-io/leaf/dialog';
import { CheckmarkIcon } from '~/ui/icons/checkmark';
import type { FilterOptions } from '~/domain/availability/index.server';

export type FilterPickerProps = {
  filterOptions: FilterOptions;
  activeLanguages: string[];
  activeLocations: string[];
  onClose: () => void;
  /** Called once the visitor presses Done. Receives whether the selection
   *  actually changed so callers can fire analytics events. */
  onApply?: (info: { changedSelection: boolean }) => void;
  /**
   * Set when the picker opens on top of another dialog — it then darkens the
   * layer below without blurring it a second time. Only the booking dialog
   * opens it that way; the experience page and the checkout steps open it
   * over the page itself.
   */
  stacked?: boolean;
};

/**
 * Modal dialog used by both the checkout tickets-and-time step and the
 * experience booking widget to filter events by language and location.
 *
 * The picker reads/writes the same `language` and `locationId` query params
 * the surrounding loaders consume — submitting via `useSubmit({ method:
 * 'get' })` triggers a loader refetch with the new selection applied.
 */
export function FilterPicker({
  filterOptions,
  activeLanguages,
  activeLocations,
  onClose,
  onApply,
  stacked,
}: FilterPickerProps) {
  const submit = useSubmit();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);

  const [selectedLanguages, setSelectedLanguages] =
    useState<string[]>(activeLanguages);
  const [selectedLocations, setSelectedLocations] =
    useState<string[]>(activeLocations);

  function handleApply() {
    if (!formRef.current) return;

    const languagesChanged =
      selectedLanguages.length !== activeLanguages.length ||
      selectedLanguages.some((l) => !activeLanguages.includes(l));
    const locationsChanged =
      selectedLocations.length !== activeLocations.length ||
      selectedLocations.some((l) => !activeLocations.includes(l));
    onApply?.({ changedSelection: languagesChanged || locationsChanged });

    const params = new URLSearchParams(searchParams);
    params.delete('language');
    params.delete('locationId');
    const formData = new FormData(formRef.current);
    for (const [key, value] of formData) {
      params.append(key, value as string);
    }
    onClose();
    submit(params, { method: 'get', preventScrollReset: true });
  }

  function toggleLanguage(value: string) {
    setSelectedLanguages((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }

  return (
    <Dialog
      title={
        <FormattedMessage
          defaultMessage="Filters"
          description="Filter dialog heading"
        />
      }
      size="small"
      stacked={stacked}
      onClose={handleApply}
      primaryAction={{
        appearance: 'primary',
        onClick: handleApply,
        children: (
          <FormattedMessage
            defaultMessage="Done"
            description="Filter dialog done button"
          />
        ),
      }}
    >
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          handleApply();
        }}
      >
        {selectedLanguages.map((lang) => (
          <input key={lang} type="hidden" name="language" value={lang} />
        ))}
        <Stack gap="large">
          {filterOptions.locations.length > 1 && (
            <CheckboxGroup
              label={
                <FormattedMessage
                  defaultMessage="Location <regular>({count})</regular>"
                  description="Filter dialog location label with count"
                  values={{
                    count: selectedLocations.length,
                    regular: (chunks) => (
                      <Text style="body-small-regular">{chunks}</Text>
                    ),
                  }}
                />
              }
              value={selectedLocations}
              onChange={setSelectedLocations}
              name="locationId"
            >
              {filterOptions.locations.map((option) => (
                <Checkbox key={option.value} value={option.value}>
                  {option.label}
                </Checkbox>
              ))}
            </CheckboxGroup>
          )}

          {filterOptions.languages.length > 1 && (
            <Stack gap="medium">
              <Text style="label-small" color="secondary">
                <FormattedMessage
                  defaultMessage="Language <regular>({count})</regular>"
                  description="Filter dialog language label with count"
                  values={{
                    count: selectedLanguages.length,
                    regular: (chunks) => (
                      <Text style="body-small-regular">{chunks}</Text>
                    ),
                  }}
                />
              </Text>
              <Stack direction="row" gap="xsmall" wrap>
                {filterOptions.languages.map((option) => {
                  const isSelected = selectedLanguages.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleLanguage(option.value)}
                      className={css({ cursor: 'interactive' })}
                    >
                      <Badge
                        appearance={isSelected ? 'positive' : 'neutral'}
                        {...(isSelected && {
                          icon: <CheckmarkIcon size="small" />,
                        })}
                      >
                        {option.label}
                      </Badge>
                    </button>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Stack>
      </form>
    </Dialog>
  );
}
